import { CopyButton } from "./CopyButton";

interface Endpoint {
  method: "GET" | "POST" | "PATCH" | "DELETE";
  path: string;
  desc: string;
  example: (base: string) => string;
}

const ENDPOINTS: Endpoint[] = [
  {
    method: "POST",
    path: "/scrapers",
    desc: "Create a scraper. Provide target URLs, an output schema, and an extraction config (or generate one via the UI's AI assist).",
    example: (b) =>
      `curl -X POST ${b}/scrapers \\
  -H "authorization: Bearer $AMENDIE_KEY" \\
  -H "content-type: application/json" \\
  -d '{
    "name": "quotes",
    "target_urls": ["https://quotes.toscrape.com/"],
    "fetch_mode": "static",
    "output_schema": { "isList": true, "fields": [
      { "name": "text", "type": "string", "required": true, "array": false }
    ] },
    "extraction_config": { "rootSelector": ".quote", "fields": [
      { "name": "text", "selector": ".text", "selectorType": "css", "multiple": false }
    ] }
  }'`,
  },
  {
    method: "GET",
    path: "/scrapers",
    desc: "List your scrapers.",
    example: (b) => `curl ${b}/scrapers -H "authorization: Bearer $AMENDIE_KEY"`,
  },
  {
    method: "POST",
    path: "/scrapers/{id}/run",
    desc: "Trigger a run now. Returns the created run ids.",
    example: (b) => `curl -X POST ${b}/scrapers/{id}/run -H "authorization: Bearer $AMENDIE_KEY"`,
  },
  {
    method: "GET",
    path: "/scrapers/{id}/latest",
    desc: "The most recent successful run's extracted data.",
    example: (b) => `curl ${b}/scrapers/{id}/latest -H "authorization: Bearer $AMENDIE_KEY"`,
  },
  {
    method: "GET",
    path: "/scrapers/{id}/runs",
    desc: "Run history for a scraper (status, timing, result refs).",
    example: (b) => `curl ${b}/scrapers/{id}/runs -H "authorization: Bearer $AMENDIE_KEY"`,
  },
  {
    method: "GET",
    path: "/runs/{id}/result?format=csv",
    desc: "Download a run's result. format=json returns a time-limited download URL; format=csv streams CSV inline.",
    example: (b) => `curl "${b}/runs/{id}/result?format=csv" -H "authorization: Bearer $AMENDIE_KEY"`,
  },
  {
    method: "GET",
    path: "/scrapers/{id}/changes",
    desc: "What changed between the last two runs (added / removed / changed items).",
    example: (b) => `curl ${b}/scrapers/{id}/changes -H "authorization: Bearer $AMENDIE_KEY"`,
  },
];

export function ApiDocs() {
  const base = `${location.origin}/api`;

  return (
    <div className="stack">
      <div className="section-head">
        <h1>API</h1>
      </div>

      <section className="card">
        <h3>Base URL</h3>
        <code className="keyval mono">{base}</code>
        <div className="mt">
          <CopyButton text={base} label="Copy base URL" />
        </div>
        <h3 className="mt">Authentication</h3>
        <p className="muted">
          Send an API key (create one under <strong>API keys</strong>) as a bearer token on every
          request:
        </p>
        <code className="keyval mono">authorization: Bearer &lt;your-key&gt;</code>
        <p className="muted mt">
          Every request is scoped to your tenant — you only ever see your own scrapers, runs, and
          data.
        </p>
      </section>

      {ENDPOINTS.map((e) => (
        <section className="card" key={e.method + e.path}>
          <div className="ep-head">
            <span className={`ep-method ${e.method.toLowerCase()}`}>{e.method}</span>
            <code className="mono">{e.path}</code>
            <div style={{ flex: 1 }} />
            <CopyButton text={e.example(base)} label="Copy curl" />
          </div>
          <p className="muted">{e.desc}</p>
          <pre className="code-block mono">{e.example(base)}</pre>
        </section>
      ))}
    </div>
  );
}
